import config from '@config';
import { openProdOrSubProdOrNominals } from '@molecules/BPProductTile';
import ScreenNames from '@navigation/navigationConstants';
import { NavigationProp } from '@react-navigation/native';
import AmplitudeHelper from '@utils/analytics';
import { AMPLITUDE_CONSTANTS } from '@utils/analytics/constants';
import { initialiseUserAttributes } from '@utils/engagement/engagementUtils';
import { setUserProperties } from '@utils/performance';
import { formatUnderScoreLowerCase, serverError } from '@utils/utils';
import products from '../../../product';

type Account = {
  isWithdrawalEligible?: boolean;
  isBillPaymentEligible?: boolean;
  [key: string]: any;
};

type MeApiData = {
  accounts?: Account[];
  canShowReferral?: boolean;
  employeeId?: string | number;
  isInLockPeriod?: boolean;
  referralApplicable?: boolean;
  status?: string;
  userId?: string;
  employerId?: string;
  firstName?: string;
  lastName?: string;
  isSuspended?: boolean;
  headerText?: string;
  referralButtonText?: string;
  appliedReferralCode?: string;
  successPageBanners?: any;
  lockPeriodDate?: string;
  allowEwaAutoDisburse?: boolean;
};
type Link = { url?: string };
type RenderProps = {
  meApiData?: MeApiData;
  isSuccess?: boolean;
  setAccounts: (accounts: Account[]) => void;
  setSelectedAccount: (account: Account) => void;
  setCanShowReferral: (canShowReferral?: boolean) => void;
  setEmployeeId: (employeeId?: string | number) => void;
  setIsInLockPeriod: (isInLockPeriod?: boolean) => void;
  setReferralApplicable: (referralApplicable?: boolean) => void;
  setStatus: (status?: string) => void;
  setUserId: (userId?: string) => void;
  setEmployerId: (employerId?: string) => void;
  setBPAccounts: (accounts: Account[]) => void;
  setSelectedBPAccounts: (account: Account) => void;
  userName?: string;
  setBPPhone: (info: { mobileNumber: string; name?: string }) => void;
  setIsSuspended: (isSuspended?: boolean) => void;
  setHeaderText: (text?: string) => void;
  setReferralHeaderText: (text?: string) => void;
  setAppliedReferral: (code?: string) => void;
  setTxnSuccessReferral: (banners?: any) => void;
  setAutoDisburseData: (data: { lockPeriodDate?: string; allowEwaAutoDisburse?: boolean }) => void;
  mobileNumber: string;
  deviceId?: string;
};

export function shouldRenderHomeOnSuccess(renderProps: RenderProps): void {
  const {
    meApiData,
    isSuccess,
    setAccounts,
    setSelectedAccount,
    setCanShowReferral,
    setEmployeeId,
    setIsInLockPeriod,
    setReferralApplicable,
    setStatus,
    setUserId,
    setEmployerId,
    mobileNumber,
    setBPAccounts,
    setSelectedBPAccounts,
    userName,
    setBPPhone,
    setIsSuspended,
    setHeaderText,
    setReferralHeaderText,
    setAppliedReferral,
    setTxnSuccessReferral,
    setAutoDisburseData,
    deviceId,
  } = renderProps;

  if (meApiData && isSuccess) {
    const {
      accounts,
      canShowReferral,
      employeeId,
      isInLockPeriod,
      referralApplicable,
      status,
      userId,
      employerId,
      firstName,
      lastName,
      isSuspended,
      headerText,
      referralButtonText,
      appliedReferralCode,
      successPageBanners,
      lockPeriodDate,
      allowEwaAutoDisburse,
    } = meApiData;

    setAccounts(accounts || []);
    setSelectedAccount({ ...(accounts?.filter(item => item.isWithdrawalEligible)?.[0] || {}) });
    setBPAccounts(accounts?.filter(item => item.isBillPaymentEligible) || []);
    setSelectedBPAccounts({ ...(accounts?.filter(item => item.isBillPaymentEligible)?.[0] || {}) });
    setCanShowReferral(canShowReferral);
    setEmployeeId(employeeId);
    setIsInLockPeriod(isInLockPeriod);
    setReferralApplicable(referralApplicable);
    setStatus(status);
    setUserId(userId);
    setEmployerId(employerId);
    setHeaderText(headerText);
    setReferralHeaderText(referralButtonText);
    setUserProperties(employeeId, employerId, `${firstName} ${lastName}`, deviceId);
    if (employeeId) {
      AmplitudeHelper.setUserId(employeeId?.toString(), employerId?.toString());
      AmplitudeHelper.setUserProperties({
        userId: employeeId?.toString(),
        employeeId: employeeId?.toString(),
        employerId: employerId?.toString(),
        environment: config.ENVIRONMENT,
        deviceId,
      });
    }
    initialiseUserAttributes({ userId, phoneNumber: mobileNumber, is_gajigea: true, employeeId, userName, deviceId });
    setBPPhone({ mobileNumber, name: userName });
    setIsSuspended(isSuspended);
    setAppliedReferral(appliedReferralCode);
    setTxnSuccessReferral(successPageBanners);
    setAutoDisburseData({ lockPeriodDate, allowEwaAutoDisburse });
  }
}

type ErrorType = unknown;

type Toast = {
  show: (message: string, options?: any) => void;
};

type Navigation = {
  navigate: (route: string, params?: Record<string, any>) => void;
};

export function shouldShowError(
  isError: boolean,
  error: ErrorType,
  toast: Toast,
  t: (key: string) => string,
  navigation: Navigation,
  setInitialData: () => void,
): void {
  if (isError && error) {
    serverError(error, toast, t, navigation, setInitialData);
  }
}

export function getScreenNameFromLink(
  link: Link,
  path: string = 'link/',
  stripQueryParams: boolean = false,
): string | undefined {
  const extracted = link?.url?.split(path)?.reverse()[0];
  return stripQueryParams ? extracted?.split('?')[0] : extracted;
}

export function logUTMCampagin(params): void {
  if (params) {
    const e = AMPLITUDE_CONSTANTS.utm_detected;
    AmplitudeHelper.logEvent(e.name, params);
  }
}

export function logReferralDeepLink(link: Link): void {
  const e = AMPLITUDE_CONSTANTS.app_open_via_referral;
  AmplitudeHelper.logEvent(e.name, {
    [e.attributes.link]: link?.url,
  });
}

export function logHandleLink(link: Link): void {
  const e = AMPLITUDE_CONSTANTS.eng_diag_interruption;
  AmplitudeHelper.logEvent(e.name, {
    [e.attributes.event]: formatUnderScoreLowerCase('App opened from Notifications'),
    [e.attributes.link]: link?.url,
  });
}

export function parseAndLogUTMCampaign(url: string): string {
  try {
    const queryStart = url.indexOf('?');

    if (queryStart === -1) return false;

    const queryString = url.substring(queryStart + 1);
    const queryParams = queryString.split('&');

    const params: Record<string, string> = {};

    queryParams.forEach(param => {
      const [key, value] = param.split('=');
      params[decodeURIComponent(key)] = decodeURIComponent(value || '');
    });

    // Return only if at least one UTM exists
    return Object.keys(params).length > 0 ? params : false;
  } catch {
    return false;
  }
}

type Store = {
  setDeepLinkData: (data?: string) => void;
  share: () => void;
};
type DataType = Record<string, any>;

export function handleLink(link: Link, navigation: NavigationProp<any>, data: DataType, store?: Store): void {
  if (link?.url && link?.url.length > 0) {
    logHandleLink(link);
    logUTMCampagin(parseAndLogUTMCampaign(link.url));
    const pathName = getScreenNameFromLink(link);

    // Define actions based on screen names
    const screenActions: Record<string, () => void> = {
      [ScreenNames.REFERRAL_PAGE]: () => navigation.navigate(ScreenNames.REFERRAL_PAGE),
      [ScreenNames.TransactionsTab]: () =>
        navigation.navigate(ScreenNames.HOME, { screen: ScreenNames.TransactionsTab }),
      [ScreenNames.Finansial]: () => navigation.navigate(ScreenNames.HOME, { screen: ScreenNames.Finansial }),
      [ScreenNames.PembayaranTab]: () => navigation.navigate(ScreenNames.HOME, { screen: ScreenNames.PembayaranTab }),
      [ScreenNames.PIC_LISTING]: () => navigation.navigate(ScreenNames.PIC_LISTING),
      [ScreenNames.USER_PROFILE]: () => navigation.navigate(ScreenNames.USER_PROFILE),
      [ScreenNames.GG_CHAT_BOT]: () => navigation.navigate(ScreenNames.GG_CHAT_BOT),
      [ScreenNames.GG_AUTO_DISBURSE_TERMS]: () => navigation.navigate(ScreenNames.GG_AUTO_DISBURSE_TERMS),
    };

    if (screenActions[pathName]) {
      screenActions[pathName]();
    } else if (pathName.includes('referral')) {
      logReferralDeepLink(link);
      const pathPayload = getScreenNameFromLink(link, 'referral/', true);
      store.setDeepLinkData(pathPayload);
    } else if (pathName.includes('share')) {
      store.share();
    } else {
      // Map screen names to product indices
      const screenToProductIndex: Record<string, number> = {
        'BP-05': 5,
        'BP-06': 6,
        'BP-07': 7,
        'BP-08': 8,
        'BP-10': 10,
        'BP-13': 13,
        'BP-15': 15,
        'BP-17': 17,
        'BP-31': 31,
      };
      const productIndex = screenToProductIndex[pathName];

      if (productIndex !== undefined) {
        // `products` assumed to be defined in the outer scope
        openProdOrSubProdOrNominals(products[productIndex], navigation, data);
      }
    }
  }
}

type ProductPayload = {
  id: number;
  prefix: string;
  name: string;
  canShowNew?: boolean;
  iconLink?: string;
  screenName?: string;
  canChangeNumber?: boolean;
  availability?: boolean;
  hasMoreProducts?: boolean;
};

export function checkLinkAndHandle(link: Link, navigation: NavigationProp<any>, store: Store): void {
  const screenName = getScreenNameFromLink(link);

  const apiDataMap: Record<string, ProductPayload> = {
    'BP-05': {
      id: 5,
      prefix: 'BP-05',
      name: 'Listrik prepaid',
      canShowNew: false,
      iconLink: `${config.MEDIA_STORAGE}bill-payment-category-icons/tokenPLN.png`,
      screenName: 'Token PLN',
      canChangeNumber: true,
      availability: true,
      hasMoreProducts: true,
    },
    'BP-06': {
      id: 6,
      prefix: 'BP-06',
      name: 'PDAM postpaid',
      canShowNew: false,
      iconLink: `${config.MEDIA_STORAGE}bill-payment-category-icons/pdam3.png`,
      screenName: 'PDAM',
      canChangeNumber: true,
      availability: true,
      hasMoreProducts: true,
    },
    'BP-07': {
      id: 7,
      prefix: 'BP-07',
      name: 'Paket Data prepaid',
      canShowNew: false,
      iconLink: `${config.MEDIA_STORAGE}bill-payment-category-icons/paket-data.png`,
      screenName: 'Paket Data',
      canChangeNumber: true,
      availability: true,
      hasMoreProducts: true,
    },
    'BP-08': {
      id: 8,
      prefix: 'BP-08',
      name: 'Pascabayar postpaid',
      canShowNew: false,
      iconLink: `${config.MEDIA_STORAGE}bill-payment-category-icons/pascabayar.png`,
      screenName: 'Pascabayar',
      canChangeNumber: true,
      availability: true,
      hasMoreProducts: true,
    },
    'BP-10': {
      id: 10,
      prefix: 'BP-10',
      name: 'Pulsa prepaid',
      iconLink: `${config.MEDIA_STORAGE}bill-payment-category-icons/pulsa.png`,
      screenName: 'Pulsa',
    },
    'BP-13': {
      id: 13,
      prefix: 'BP-13',
      name: 'Voucher Digital prepaid',
      iconLink: `${config.MEDIA_STORAGE}bill-payment-category-icons/voucher.png`,
      screenName: 'Voucher',
      canChangeNumber: false,
    },
    'BP-15': {
      id: 15,
      prefix: 'BP-15',
      name: 'eMoney prepaid',
      canShowNew: false,
      iconLink: `${config.MEDIA_STORAGE}bill-payment-category-icons/e-money.png`,
      screenName: 'eMoney',
      canChangeNumber: true,
      availability: true,
      hasMoreProducts: true,
    },
    'BP-17': {
      id: 17,
      prefix: 'BP-17',
      name: 'Listrik postpaid',
      canShowNew: false,
      iconLink: `${config.MEDIA_STORAGE}bill-payment-category-icons/listrik.png`,
      screenName: 'Tagihan PLN',
      canChangeNumber: true,
      availability: true,
      hasMoreProducts: true,
    },
    '31': {
      id: 31,
      prefix: 'WD-02',
      name: 'Bank Transfer',
      canShowNew: false,
      iconLink: `${config.MEDIA_STORAGE}bill-payment-category-icons/bank-transfer.png`,
      screenName: 'Transfer',
      canChangeNumber: false,
      availability: true,
      hasMoreProducts: true,
    },
  };

  const commonProperties: Partial<ProductPayload> = {
    canShowNew: false,
    canChangeNumber: true,
    availability: true,
    hasMoreProducts: true,
  };

  const payload = apiDataMap[screenName];

  if (payload) {
    const fullPayload = { ...commonProperties, ...payload };
    handleLink(link, navigation, fullPayload, store);
  } else {
    handleLink(link, navigation, null, store);
  }
}
